/**
 * TemplateManager - Manages card templates for different games
 * Handles loading, caching, and switching between templates and card types
 */

import { assetLoader } from '@utils/AssetLoader.js';
import { CardSchema } from '@templates/CardSchema.js';
import { TEMPLATE_BASE_PATH } from '@config/constants.js';

export class TemplateManager {
  constructor() {
    this.templates = new Map(); // game -> template config
    this.templateImages = new Map(); // game:cardType:side -> image
    this.currentTemplate = null;
    this.currentCardType = null;
  }

  /**
   * Load a template for a specific game
   * @param {string} gameId - Game identifier (e.g., 'slay-the-spire')
   * @returns {Promise<Object>} Loaded template config
   */
  async loadTemplate(gameId) {
    console.log(`[TemplateManager] Loading template for: ${gameId}`);

    // Check if already loaded
    if (this.templates.has(gameId)) {
      console.log(`[TemplateManager] Template already loaded: ${gameId}`);
      return this.templates.get(gameId);
    }

    try {
      // Load template config
      const configUrl = `${TEMPLATE_BASE_PATH}${gameId}/config.json`;
      const config = await assetLoader.loadJSON(configUrl);

      // Validate template config
      const validation = CardSchema.validate(config);
      if (!validation.valid) {
        console.error(`[TemplateManager] Template validation failed:`, validation.errors);
        throw new Error(`Invalid template config for ${gameId}: ${validation.errors.join(', ')}`);
      }

      // Load template images
      await this._loadTemplateImages(gameId, config);

      // Load fonts if specified
      if (config.fonts) {
        await this._loadTemplateFonts(config.fonts);
      }

      // Cache template
      this.templates.set(gameId, config);
      console.log(`[TemplateManager] ✓ Template loaded successfully: ${gameId}`);

      return config;
    } catch (error) {
      console.error(`[TemplateManager] Failed to load template: ${gameId}`, error);
      throw error;
    }
  }

  /**
   * Load all images for a template (card types and sides)
   * @private
   */
  async _loadTemplateImages(gameId, config) {
    const imagesToLoad = [];

    // Check if template uses card types
    if (config.cardTypes) {
      // Load images for each card type
      for (const [typeName, typeData] of Object.entries(config.cardTypes)) {
        const imageUrl = `${TEMPLATE_BASE_PATH}${gameId}/${typeData.template}`;
        imagesToLoad.push({
          type: 'image',
          url: imageUrl,
          key: `${gameId}:${typeName}:front`
        });

        // For Slay the Spire, front and back use the same template
        if (config.sides.back?.usesCardType) {
          imagesToLoad.push({
            type: 'image',
            url: imageUrl,
            key: `${gameId}:${typeName}:back`
          });
        }
      }
    } else {
      // Load static front/back templates
      if (config.sides.front?.template) {
        const frontUrl = `${TEMPLATE_BASE_PATH}${gameId}/${config.sides.front.template}`;
        imagesToLoad.push({
          type: 'image',
          url: frontUrl,
          key: `${gameId}:static:front`
        });
      }

      if (config.sides.back?.template) {
        const backUrl = `${TEMPLATE_BASE_PATH}${gameId}/${config.sides.back.template}`;
        imagesToLoad.push({
          type: 'image',
          url: backUrl,
          key: `${gameId}:static:back`
        });
      }
    }

    // Load all images
    console.log(`[TemplateManager] Loading ${imagesToLoad.length} template images...`);
    const results = await assetLoader.loadMultiple(
      imagesToLoad.map(item => ({ type: item.type, url: item.url })),
      (loaded, total) => {
        console.log(`[TemplateManager] Progress: ${loaded}/${total} images loaded`);
      }
    );

    // Store images in map
    results.forEach((result, index) => {
      if (result.success) {
        const key = imagesToLoad[index].key;
        this.templateImages.set(key, result.data);
        console.log(`[TemplateManager] ✓ Stored image: ${key}`);
      } else {
        console.error(`[TemplateManager] ✗ Failed to load image:`, result.url, result.error);
      }
    });
  }

  /**
   * Load fonts specified in template config
   * @private
   */
  async _loadTemplateFonts(fonts) {
    console.log(`[TemplateManager] Loading template fonts...`);

    for (const [fontKey, fontData] of Object.entries(fonts)) {
      try {
        if (fontData.weights && Array.isArray(fontData.weights)) {
          // Load each font weight
          for (const weight of fontData.weights) {
            await assetLoader.loadFont(
              fontData.family,
              fontData.path,
              { weight: weight }
            );
          }
        } else {
          // Load default weight
          await assetLoader.loadFont(fontData.family, fontData.path);
        }
        console.log(`[TemplateManager] ✓ Font loaded: ${fontData.family}`);
      } catch (error) {
        console.warn(`[TemplateManager] Failed to load font: ${fontData.family}`, error);
        // Continue even if font fails - fallback fonts will be used
      }
    }
  }

  /**
   * Set the current active template
   * @param {string} gameId - Game identifier
   * @returns {Promise<Object>} Template config
   */
  async setCurrentTemplate(gameId) {
    if (!this.templates.has(gameId)) {
      await this.loadTemplate(gameId);
    }

    this.currentTemplate = this.templates.get(gameId);
    console.log(`[TemplateManager] Current template set to: ${gameId}`);

    // Set default card type if template uses card types
    if (this.currentTemplate.cardTypes) {
      const firstType = Object.keys(this.currentTemplate.cardTypes)[0];
      this.currentCardType = firstType;
      console.log(`[TemplateManager] Default card type set to: ${firstType}`);
    }

    return this.currentTemplate;
  }

  /**
   * Get template image for specific card type and side
   * @param {string} gameId - Game identifier
   * @param {string} cardType - Card type (e.g., 'Attack', 'Skill', 'Power')
   * @param {string} side - 'front' or 'back'
   * @returns {HTMLImageElement|null}
   */
  getTemplateImage(gameId, cardType, side = 'front') {
    const key = cardType ? `${gameId}:${cardType}:${side}` : `${gameId}:static:${side}`;
    const image = this.templateImages.get(key);

    if (!image) {
      console.warn(`[TemplateManager] Template image not found: ${key}`);
    }

    return image || null;
  }

  /**
   * Get the current active template config
   * @returns {Object|null}
   */
  getCurrentTemplate() {
    return this.currentTemplate;
  }

  /**
   * Get current card type
   * @returns {string|null}
   */
  getCurrentCardType() {
    return this.currentCardType;
  }

  /**
   * Set current card type
   * @param {string} cardType - Card type name
   */
  setCurrentCardType(cardType) {
    if (!this.currentTemplate) {
      console.warn('[TemplateManager] No template loaded');
      return;
    }

    if (!this.currentTemplate.cardTypes || !this.currentTemplate.cardTypes[cardType]) {
      console.warn(`[TemplateManager] Invalid card type: ${cardType}`);
      return;
    }

    this.currentCardType = cardType;
    console.log(`[TemplateManager] Card type changed to: ${cardType}`);
  }

  /**
   * Get available card types for current template
   * @returns {Array<string>}
   */
  getAvailableCardTypes() {
    if (!this.currentTemplate || !this.currentTemplate.cardTypes) {
      return [];
    }

    return Object.keys(this.currentTemplate.cardTypes);
  }

  /**
   * Get list of all loaded templates
   * @returns {Array<{id: string, name: string, description: string}>}
   */
  getLoadedTemplates() {
    return Array.from(this.templates.entries()).map(([id, config]) => ({
      id,
      name: config.name,
      description: config.description || ''
    }));
  }

  /**
   * Get template by ID
   * @param {string} gameId - Game identifier
   * @returns {Object|null}
   */
  getTemplate(gameId) {
    return this.templates.get(gameId) || null;
  }

  /**
   * Check if a template is loaded
   * @param {string} gameId - Game identifier
   * @returns {boolean}
   */
  isTemplateLoaded(gameId) {
    return this.templates.has(gameId);
  }

  /**
   * Get default card state for current template
   * @returns {Object}
   */
  getDefaultCardState() {
    if (!this.currentTemplate) {
      console.warn('[TemplateManager] No template loaded');
      return {};
    }

    return CardSchema.getDefaultState(this.currentTemplate.properties);
  }

  /**
   * Validate card state against current template
   * @param {Object} cardState - Card state to validate
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  validateCardState(cardState) {
    if (!this.currentTemplate) {
      return { valid: false, errors: ['No template loaded'] };
    }

    return CardSchema.validateCardState(cardState, this.currentTemplate.properties);
  }

  /**
   * Sanitize card state according to current template rules
   * @param {Object} cardState - Card state to sanitize
   * @returns {Object}
   */
  sanitizeCardState(cardState) {
    if (!this.currentTemplate) {
      return cardState;
    }

    return CardSchema.sanitizeCardState(cardState, this.currentTemplate.properties);
  }

  /**
   * Get layers for a specific side of the card
   * @param {string} side - 'front' or 'back'
   * @returns {Array<Object>}
   */
  getLayers(side = 'front') {
    if (!this.currentTemplate || !this.currentTemplate.sides[side]) {
      return [];
    }

    return this.currentTemplate.sides[side].layers || [];
  }

  /**
   * Get card dimensions from template
   * @returns {{width: number, height: number, aspectRatio: number}}
   */
  getCardDimensions() {
    if (!this.currentTemplate) {
      return { width: 512, height: 768, aspectRatio: 0.667 };
    }

    return this.currentTemplate.cardDimensions;
  }

  /**
   * Clear all cached templates and images
   */
  clearCache() {
    this.templates.clear();
    this.templateImages.clear();
    this.currentTemplate = null;
    this.currentCardType = null;
    console.log('[TemplateManager] Cache cleared');
  }

  /**
   * Reload a template (useful for development/debugging)
   * @param {string} gameId - Game identifier
   */
  async reloadTemplate(gameId) {
    console.log(`[TemplateManager] Reloading template: ${gameId}`);

    // Clear from cache
    this.templates.delete(gameId);

    // Clear template images for this game
    const keysToDelete = [];
    for (const key of this.templateImages.keys()) {
      if (key.startsWith(`${gameId}:`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.templateImages.delete(key));

    // Clear asset loader cache
    assetLoader.clearCache(`${TEMPLATE_BASE_PATH}${gameId}/config.json`);

    // Reload
    return await this.loadTemplate(gameId);
  }
}

// Create singleton instance
export const templateManager = new TemplateManager();

console.log('TemplateManager loaded');
